import React, { createContext, useContext, useState } from "react"
import { cn } from "./utils"
import { ChevronDown } from "lucide-react"

const AccordionContext = createContext(null)

const Accordion = ({
  children,
  type = "single",
  collapsible = false,
  value,
  defaultValue,
  onValueChange,
  className,
}) => {
  const [state, setState] = useState(defaultValue || null)

  const contextValue = {
    type,
    collapsible,
    value: value !== undefined ? value : state,
    onValueChange: onValueChange || ((value) => setState(value)),
  }

  return (
    <AccordionContext.Provider value={contextValue}>
      <div className={cn("w-full", className)}>{children}</div>
    </AccordionContext.Provider>
  )
}

const useAccordionContext = () => {
  const context = useContext(AccordionContext)
  if (!context) {
    throw new Error("Accordion components must be used within an Accordion")
  }
  return context
}

const AccordionItem = React.forwardRef(({ className, value, children, ...props }, ref) => {
  const { value: selectedValue, onValueChange, type, collapsible } = useAccordionContext()
  const isOpen = type === "single" ? selectedValue === value : selectedValue?.includes(value)

  const handleClick = () => {
    if (type === "single") {
      if (isOpen && collapsible) {
        onValueChange(null)
      } else {
        onValueChange(value)
      }
    } else if (type === "multiple") {
      if (isOpen) {
        onValueChange(selectedValue.filter((v) => v !== value))
      } else {
        onValueChange([...(selectedValue || []), value])
      }
    }
  }

  return (
    <div
      ref={ref}
      data-state={isOpen ? "open" : "closed"}
      className={cn("border-b", className)}
      {...props}
      data-value={value}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            isOpen,
            onToggle: handleClick,
          })
        }
        return child
      })}
    </div>
  )
})
AccordionItem.displayName = "AccordionItem"

const AccordionTrigger = React.forwardRef(({ className, children, isOpen, onToggle, ...props }, ref) => {
  return (
    <div className="flex">
      <button
        ref={ref}
        onClick={onToggle}
        className={cn(
          `${className} flex flex-1 items-center justify-between font-medium transition-all hover:text-[#FF6B00] [&[data-state=open]>div>svg]:rotate-180 py-4`,
        )}
        data-state={isOpen ? "open" : "closed"}
        {...props}
      >
        {children}
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[#FF6B00] opacity-70 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
    </div>
  )
})
AccordionTrigger.displayName = "AccordionTrigger"

const AccordionContent = React.forwardRef(({ className, children, isOpen, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.4, 0, 0.2, 1)]",
        isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0",
        className,
      )}
      {...props}
    >
      <div className="pb-4 pt-0 pl-2 text-sm border-[#FF6B00]/30">
        {children}
      </div>
    </div>
  )
})

AccordionContent.displayName = "AccordionContent"

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
